import { useCallback, useEffect, useState } from "react";
import { Download, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";

const STATUS_STYLES = {
    pending: "border-amber-300 bg-amber-50 text-amber-700",
    contacted: "border-blue-300 bg-blue-50 text-blue-700",
    confirmed: "border-emerald-300 bg-emerald-50 text-emerald-700",
    cancelled: "border-slate-300 bg-slate-100 text-slate-500",
};

const fmtDate = (iso) => {
    if (!iso) return "—";
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
};

export default function LeadsTab() {
    const [leads, setLeads] = useState([]);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState([]);
    const [busy, setBusy] = useState(false);
    const [exporting, setExporting] = useState(false);

    const load = useCallback(async () => {
        try {
            const [leadsRes, statsRes] = await Promise.all([api.get("/api/admin/leads"), api.get("/api/admin/stats")]);
            setLeads(leadsRes.data?.leads || []);
            setStats(statsRes.data || null);
        } catch (e) {
            toast.error(apiError(e, "Failed to load leads"));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const setStatus = async (lead, status) => {
        try {
            await api.patch(`/api/admin/leads/${lead.id}`, { status });
            setLeads((rows) => rows.map((r) => (r.id === lead.id ? { ...r, status } : r)));
            toast.success(`${lead.name} marked as ${status}`);
            const { data } = await api.get("/api/admin/stats");
            setStats(data);
        } catch (e) {
            toast.error(apiError(e, "Failed to update status"));
        }
    };

    const toggleOne = (id) =>
        setSelected((sel) => (sel.includes(id) ? sel.filter((s) => s !== id) : [...sel, id]));

    const allSelected = leads.length > 0 && selected.length === leads.length;
    const toggleAll = () => setSelected(allSelected ? [] : leads.map((l) => l.id));

    const deleteSelected = async () => {
        if (!selected.length) return;
        if (!window.confirm(`Delete ${selected.length} selected lead${selected.length > 1 ? "s" : ""}? This cannot be undone.`)) return;
        setBusy(true);
        try {
            const { data } = await api.post("/api/admin/leads/bulk-delete", { ids: selected });
            toast.success(`${data.deleted} lead${data.deleted === 1 ? "" : "s"} deleted`);
            setSelected([]);
            await load();
        } catch (e) {
            toast.error(apiError(e, "Failed to delete leads"));
        } finally {
            setBusy(false);
        }
    };

    const exportCsv = async () => {
        setExporting(true);
        try {
            const res = await api.get("/api/admin/leads/export", { responseType: "blob" });
            const url = URL.createObjectURL(res.data);
            const a = document.createElement("a");
            a.href = url;
            a.download = "work-island-leads.csv";
            a.click();
            URL.revokeObjectURL(url);
            toast.success("Leads exported — Excel/Sheets mein kholein");
        } catch (e) {
            toast.error(apiError(e, "Export failed"));
        } finally {
            setExporting(false);
        }
    };

    if (loading) {
        return (
            <div className="flex justify-center py-20" data-testid="leads-loading">
                <div className="h-9 w-9 animate-spin rounded-full border-[3px] border-slate-200 border-t-brand-blue" />
            </div>
        );
    }

    return (
        <div data-testid="leads-tab">
            {stats && (
                <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
                    {[
                        { label: "Total Leads", value: stats.total, tid: "stat-total" },
                        { label: "Pending", value: stats.pending, tid: "stat-pending" },
                        { label: "Confirmed", value: stats.confirmed, tid: "stat-confirmed" },
                        { label: "New Today", value: stats.today, tid: "stat-today" },
                    ].map((s) => (
                        <div key={s.tid} data-testid={s.tid} className="rounded-2xl border border-slate-200/80 bg-white px-5 py-4">
                            <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-ink/45">{s.label}</p>
                            <p className="mt-1 text-3xl font-bold tracking-tight text-ink">{s.value}</p>
                        </div>
                    ))}
                </div>
            )}

            {leads.length > 0 && (
                <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                    <p className="text-sm font-medium text-ink/55" data-testid="leads-selected-count">
                        {selected.length > 0 ? `${selected.length} selected` : `${leads.length} total leads`}
                    </p>
                    <div className="flex items-center gap-2.5">
                        {selected.length > 0 && (
                            <button
                                data-testid="delete-selected-btn"
                                onClick={deleteSelected}
                                disabled={busy}
                                className="inline-flex items-center gap-1.5 rounded-full border border-red-300 bg-red-50 px-5 py-2.5 text-[13px] font-bold text-red-600 transition-colors hover:bg-red-100 disabled:opacity-50"
                            >
                                <Trash2 size={14} strokeWidth={2.5} />
                                {busy ? "Deleting…" : `Delete Selected (${selected.length})`}
                            </button>
                        )}
                        <button
                            data-testid="export-leads-btn"
                            onClick={exportCsv}
                            disabled={exporting}
                            className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-5 py-2.5 text-[13px] font-bold text-ink/70 transition-colors hover:border-brand-teal/60 hover:text-ink disabled:opacity-50"
                        >
                            <Download size={14} strokeWidth={2.5} />
                            {exporting ? "Exporting…" : "Export CSV"}
                        </button>
                    </div>
                </div>
            )}

            {leads.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-slate-300 bg-white py-16 text-center" data-testid="leads-empty">
                    <p className="text-sm font-semibold text-ink/50">No demo bookings yet. New leads will appear here.</p>
                </div>
            ) : (
                <div className="overflow-x-auto rounded-2xl border border-slate-200/80 bg-white" data-testid="leads-table">
                    <table className="w-full min-w-[960px] text-left text-sm">
                        <thead>
                            <tr className="border-b border-slate-200 text-[11px] font-bold uppercase tracking-[0.14em] text-ink/45">
                                <th className="px-4 py-3.5">
                                    <input
                                        type="checkbox"
                                        data-testid="select-all-leads"
                                        checked={allSelected}
                                        onChange={toggleAll}
                                        aria-label="Select all leads"
                                        className="h-4 w-4 cursor-pointer accent-[#2B5CAB]"
                                    />
                                </th>
                                <th className="px-5 py-3.5">Name</th>
                                <th className="px-5 py-3.5">Mobile</th>
                                <th className="px-5 py-3.5">Email</th>
                                <th className="px-5 py-3.5">Company</th>
                                <th className="px-5 py-3.5">Seats</th>
                                <th className="px-5 py-3.5">Demo Date</th>
                                <th className="px-5 py-3.5">Booked On</th>
                                <th className="px-5 py-3.5">Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {leads.map((lead) => (
                                <tr
                                    key={lead.id}
                                    data-testid={`lead-row-${lead.id}`}
                                    className={`border-b border-slate-100 last:border-0 hover:bg-slate-50/60 ${selected.includes(lead.id) ? "bg-brand-blue/5" : ""}`}
                                >
                                    <td className="px-4 py-3.5">
                                        <input
                                            type="checkbox"
                                            data-testid={`lead-select-${lead.id}`}
                                            checked={selected.includes(lead.id)}
                                            onChange={() => toggleOne(lead.id)}
                                            aria-label={`Select ${lead.name}`}
                                            className="h-4 w-4 cursor-pointer accent-[#2B5CAB]"
                                        />
                                    </td>
                                    <td className="px-5 py-3.5 font-bold text-ink">{lead.name}</td>
                                    <td className="px-5 py-3.5 font-medium text-ink/70">
                                        <a href={`tel:+91${lead.mobile}`} className="hover:text-brand-blue">{lead.mobile}</a>
                                    </td>
                                    <td className="px-5 py-3.5 font-medium text-ink/70">{lead.email || "—"}</td>
                                    <td className="px-5 py-3.5 font-medium text-ink/70">{lead.company || "—"}</td>
                                    <td className="px-5 py-3.5 font-medium text-ink/70">{lead.seats}</td>
                                    <td className="px-5 py-3.5 font-semibold text-ink">{fmtDate(lead.demo_date)}</td>
                                    <td className="px-5 py-3.5 font-medium text-ink/50">{fmtDate(lead.created_at)}</td>
                                    <td className="px-5 py-3.5">
                                        <select
                                            data-testid={`lead-status-${lead.id}`}
                                            value={lead.status}
                                            onChange={(e) => setStatus(lead, e.target.value)}
                                            className={`cursor-pointer rounded-full border px-3 py-1.5 text-xs font-bold capitalize outline-none ${STATUS_STYLES[lead.status] || STATUS_STYLES.pending}`}
                                        >
                                            {Object.keys(STATUS_STYLES).map((s) => (
                                                <option key={s} value={s} className="text-ink">
                                                    {s.charAt(0).toUpperCase() + s.slice(1)}
                                                </option>
                                            ))}
                                        </select>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
